import React, { useEffect, useRef, useState } from 'react';

const ReservationWidget = () => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const scriptLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (widgetInitializedRef.current) return;
    
    // Generate unique ID for this widget instance
    const embedId = `embed-${Math.random().toString(36).substr(2, 9)}`;
    
    const initializeWidget = () => {
      // Prevent double initialization
      if (widgetInitializedRef.current && containerRef.current?.children.length > 0) return;
      
      try {
        if (containerRef.current && window.muEmbed && typeof window.muEmbed === 'function') {
          // Clear container first to prevent duplicates
          containerRef.current.innerHTML = '';
          
          // Use the embed ID from the container
          const containerId = containerRef.current.id;
          
          window.muEmbed({
            embedType: 'reservation-widget',
            accommodationId: 14174,
            token: '9f5514548de57b87da267a67eb15d5ee',
            settings: {
              primary_color: 'rgba(184,115,51,1)',
              secondary_color: '#7A4B21',
              background_color: 'rgba(255,255,255,1)',
              text_color: 'rgba(0,0,0,1)'
            }
          }, containerId);
          widgetInitializedRef.current = true;
        }
      } catch (err) {
        console.error('Error initializing reservation widget:', err);
        setError('Failed to initialize reservation widget');
      }
    };
    
    if (!containerRef.current) return;

    // Set the ID and attributes on the container first
    containerRef.current.id = embedId;
    containerRef.current.setAttribute('data-mu-embed-origin', 'https://www.megaubytovanie.sk/');
    containerRef.current.style.width = '100%';

    // Check if script is already loaded
    const existingScript = document.querySelector('script[src*="muEmbed.js"]');
    
    const loadScript = () => {
      if (scriptLoadedRef.current) {
        // Script already loaded, just initialize
        setTimeout(initializeWidget, 300);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://www.megaubytovanie.sk/muEmbed.js';
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        scriptLoadedRef.current = true;
        // Wait a bit for the function to be available
        setTimeout(() => {
          if (window.muEmbed) {
            initializeWidget();
          } else {
            setError('Widget function not available');
          }
        }, 300);
      };
      
      script.onerror = (err) => {
        console.error('Failed to load reservation widget script:', err);
        setError('Failed to load reservation widget');
      };
      
      // Add to head instead of body for better compatibility
      const head = document.head || document.getElementsByTagName('head')[0];
      head.appendChild(script);
    };

    if (existingScript) {
      // Script tag exists, wait for it to load
      if (window.muEmbed && typeof window.muEmbed === 'function') {
        setTimeout(initializeWidget, 300);
      } else {
        // Wait for script to load
        let attempts = 0;
        const maxAttempts = 50;
        
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.muEmbed && typeof window.muEmbed === 'function') {
            clearInterval(checkInterval);
            setTimeout(initializeWidget, 300);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            setError('Widget script timeout');
          }
        }, 100);
        
        return () => clearInterval(checkInterval);
      }
    } else {
      // Load the script
      loadScript();
    }
    
    // Cleanup function
    return () => {
      // Don't reset widgetInitializedRef here to prevent re-initialization on unmount
    };
  }, []);

  if (error) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Rezervačný widget sa nepodarilo načítať. Skúste obnoviť stránku.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="w-full min-h-[400px]"
    />
  );
};

export default ReservationWidget;
